<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.user_campaign') }}
      <template v-slot:right>
        <b-button @click="goToCampaignCreate" size="sm" class="ml-1" variant="success">{{ $t('btn.add_new_campaign') }}</b-button>
      </template>
    </page-header>

    <page-card>
      <template v-slot:header>
        {{ $t('page.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid sygni-grid--standard-width-datepicker">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="id"
                  :label="$t('filters.label.campaign.id')"
                  :placeholder="$t('filters.placeholder.message.id')">
              </filter-input>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="name"
                  :label="$t('filters.label.campaign.name')"
                  :placeholder="$t('filters.placeholder.message.name')">
              </filter-input>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="title"
                  :label="$t('filters.label.campaign.title')"
                  :placeholder="$t('filters.placeholder.message.title')">
              </filter-input>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="sendersEmail"
                  :label="$t('filters.label.campaign.sendersEmail')"
                  :placeholder="$t('filters.placeholder.campaign.sendersEmail')">
              </filter-input>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="sendersName"
                  :label="$t('filters.label.campaign.sendersName')"
                  :placeholder="$t('filters.placeholder.campaign.sendersName')">
              </filter-input>
            </div>
            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.label.campaign.created_at')">
              </filter-date-range-picker>
            </div>
          </div>
          <div class="row">
            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="sendingTime"
                  open-direction="left"
                  :label="$t('filters.label.campaign.sending_time')">
              </filter-date-range-picker>
            </div>
            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="sentDate"
                  open-direction="left"
                  :label="$t('filters.label.campaign.sent_date')">
              </filter-date-range-picker>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="selectOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('filters.label.campaign.status')"
                  :placeholder="$t('filters.placeholder.campaign.status')">
              </filter-select>
            </div>
          </div> <!-- #row-->
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
      </template>
    </page-card>
    <page-card>
      <template v-slot:header>
        {{ $t('page.list')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid sygni-grid--standard-width-datepicker">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :empty-text="$t('table.no_results')"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(id)="data">
                {{ data.item.id | shortId }}
              </template>

              <template #cell(createdAt)="data">
                {{ data.item.createdAt | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(sendingTime)="data">
                {{ data.item.sendingTime | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(sentDate)="data">
                {{ data.item.sentDate | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="primary" v-if="data.item.status === 'draft'">{{$t('campaign_status_'+ data.value )}}</b-badge>
                <b-badge pill variant="success" v-if="data.item.status === 'active'">{{$t('campaign_status_'+ data.value )}}</b-badge>
                <b-badge pill variant="warning" v-if="data.item.status === 'sent'">{{$t('campaign_status_'+ data.value )}}</b-badge>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <b-button variant="primary" size="sm" @click="openModal('show', data.item)">{{ $t('table.btn.campaign.show_details') }}</b-button>
                  <b-button variant="success" size="sm" @click="goToCampaignCreate('edit', data.item)">{{ $t('table.btn.campaign.edit') }}</b-button>
                  <b-button variant="warning" size="sm" @click="goToCampaignCreate('', data.item)">{{ $t('table.btn.campaign.duplicate') }}</b-button>
                  <b-button v-if="data.item.status !== 'sent' && data.item.status !== 'active'" variant="danger" size="sm" @click="deleteCampaign(data.item)">{{ $t('table.btn.campaign.delete') }}</b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>

  </div>
</template>

<script>
import GridMixin from "../../../components/grid/GridMixin";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";
import ShowCampaignModal from "./ShowCampaignModal";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import alertify from "alertifyjs";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    GridPagination,
    FilterInput,
    FilterSelect,
    FilterDateRangePicker
  },
  data() {
    return {
      gridGetUrl: '/api/inbox/campaigns',
      defaultFilters: '',
      fields: [
        {key: 'id', label: this.$t('table.header.campaign.id'), sortable: false},
        {key: 'name', label: this.$t('table.header.campaign.name'), sortable: true},
        {key: 'title', label: this.$t('table.header.campaign.title'), sortable: true},
        {key: 'sendersEmail', label: this.$t('table.header.campaign.senders_email'), sortable: true},
        {key: 'sendersName', label: this.$t('table.header.campaign.senders_name'), sortable: true},
        {key: 'title', label: this.$t('table.header.campaign.title'), sortable: true},
        {key: 'createdAt', label: this.$t('table.header.campaign.created_at'), sortable: true},
        {key: 'sendingTime', label: this.$t('table.header.campaign.sending_time'), sortable: true},
        {key: 'sentDate', label: this.$t('table.header.campaign.sent_date'), sortable: true},
        {key: 'status', label: this.$t('table.header.campaign.campaign_status'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions'), sortable: false}
      ],
      perPage: 10,
      currentPage: 1,
      orderColumn: 'createdAt',
      orderDirection: false,
      createModalInstance: null,
      selectOptions: {
        status: [
          {value: 'draft', text: this.$t('option.campaign.draft')},
          {value: 'active', text: this.$t('option.campaign.active')},
        ]
      },
    };
  },
  mounted() {
    this.$on('refreshGrid', () => {
      this.applyFilters();
    })
  },
  methods: {
    openModal(type, data) {
      switch (type) {
        case('show'): {
          this.$store.commit('modal/openModal', {parent: this, modal: ShowCampaignModal, modalName: 'showModal', data});
          break;
        }
        // case('edit'): {
        //   this.$store.commit('modal/openModal', {
        //     parent: this,
        //     modal: EditQuestionModal,
        //     modalName: 'createModal',
        //     data,
        //     props: {apiKey: this.apiKey, selectOptions: this.selectOptions}
        //   });
        //   break;
        // }
      }
    },
    deleteCampaign(campaign) {
      this.$bvModal.msgBoxConfirm(`${this.$t('modal.title.remove_campaign_name')}: ${campaign.name}`, {
        title: this.$t('modal.title.confirm_remove_campaign'),
        okTitle: this.$t('btn.confirm'),
        cancelTitle: this.$t('btn.cancel'),
        okVariant: 'success',
      })
      .then(value => {
        if (value) {
          this.$store.dispatch('userCampaign/deleteCampaign', campaign.id).then(() => {
            this.loadItems();
            alertify.success(this.$t('notify.resale.confirmation.success'));
          })
          .catch(() => {
            alertify.error(this.$t('notify.resale.confirmation.error'));
          });
        }
      });
    },
    goToCampaignCreate(actionType, campaign) {
      const params = {
        actionType: actionType,
        ...campaign
      }

      this.$router.push({ name: `user-campaign-create`, params: params });
    }
  }
}
</script>
